import { useEffect, useState, type ReactNode } from 'react';

import {
  App as AntdApp,
  ApiError,
  ApiErrorFormatterProvider,
  BlockedReasonProvider,
  BookoraThemeProvider,
} from '@bookora/ui';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';

// The module, not the `@/guidance` barrel: screens' tests mock the barrel.
import { noteMutationSuccess } from '@/guidance/inline-tour-host';
import { struggleBlockedPress } from '@/guidance/struggle/collectors';
import { env } from '@/lib/env';
import { failureMessage, noteApiFailure, registerFeedbackApi } from '@/lib/feedback';

/**
 * Everything that must wrap the router: the query cache (whose successful
 * mutations tell guidance's struggle detector the person is getting on), the
 * AntD RTL/Persian theme, and the root listener for presses on blocked kit
 * controls (the struggle detector's blocked-press collector).
 *
 * The theme mount is `@bookora/ui`'s `BookoraThemeProvider` rather than a local
 * `ConfigProvider`. It is the same combination in both panels —
 * `direction="rtl"`, `locale={faIR}`, `bookoraAntdTheme(mode)`, plus AntD's
 * `App` context — and a panel that assembles its own eventually forgets one of
 * them. Forgetting `App` is the interesting failure: `message.success(...)`
 * then renders default-blue LTR AntD in the corner of a pink RTL panel.
 *
 * ── THERE IS NO THEME SHIM HERE, DELIBERATELY ───────────────────────────────
 *
 * This file used to mount a `LightByDefault` effect that normalised anything
 * that was not an explicit light or dark. It existed only to neutralise the
 * `'system'` sentinel that `useThemeMode` once defaulted to, and it is gone:
 * the kit's `resolveThemePreference` now applies the same rule on every read,
 * and — unlike the effect — never writes back. A second opinion about the
 * theme, held one tick behind the first, is exactly how a flash of the wrong
 * canvas gets reintroduced. The panel contributes its storage key and nothing
 * else; see `index.html` for the pre-paint half.
 */

function createQueryClient(): QueryClient {
  return new QueryClient({
    // Any save that went through is progress: the struggle detector's counts
    // (repeated refusals, validation bursts, blocked presses) start afresh,
    // so a person who got there on their own is not offered help for it.
    // Any save that failed is noted once — its tracking code for support, its
    // code for «the same refusal twice» — however the screen then shows it
    // (most use the kit's `showError`, which `lib/feedback` never sees).
    mutationCache: new MutationCache({
      onSuccess: () => noteMutationSuccess(),
      onError: (error) => noteApiFailure(error),
    }),
    defaultOptions: {
      queries: {
        // A panel is used all day with many tabs open; refetching every list on
        // every focus is a wasted round trip on a slow Iranian connection.
        // Individual screens opt in where freshness matters.
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error) => {
          // 4xx means the server understood and refused: a wrong permission, a
          // missing row, a bad filter. Retrying cannot change the answer and
          // just delays the Persian message the user needs to read.
          if (error instanceof ApiError && error.statusCode >= 400 && error.statusCode < 500) {
            return false;
          }
          return failureCount < 2;
        },
      },
      mutations: { retry: 0 },
    },
  });
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <BookoraThemeProvider storageKey={env.themeStorageKey}>
        <FeedbackBridge />
        {/* A press on a blocked kit control is a struggle signal (critic C6):
            the kit reports every swallowed press — clicks, taps and a
            DisabledReason wrapper's Enter/Space — to the nearest listener.
            A press the capture listener in `struggle/collectors.ts` already
            counted is not counted twice. It only listens: no reason is set. */}
        <BlockedReasonProvider onBlockedPress={struggleBlockedPress}>
          {/* Most screens report a failed save through the kit's
              `useApiFeedback().showError`: it words a refusal the way
              `toast.error` does — a 403 names the missing permission and,
              by role, what to do (critic C18). */}
          <ApiErrorFormatterProvider format={failureMessage}>{children}</ApiErrorFormatterProvider>
        </BlockedReasonProvider>
      </BookoraThemeProvider>
    </QueryClientProvider>
  );
}

/**
 * Publishes AntD's themed `message`/`modal`/`notification` instances to the
 * module-level helpers in `lib/feedback.ts`, so code outside React — an axios
 * interceptor, a store action — can raise a toast that still looks like the
 * rest of the panel. Renders nothing.
 */
function FeedbackBridge() {
  const { message, modal, notification } = AntdApp.useApp();

  useEffect(() => {
    registerFeedbackApi({ message, modal, notification });
  }, [message, modal, notification]);

  return null;
}
